import React, { useState, useEffect, useMemo } from "react";
import { Clock, CheckCircle, Folder, FileText, Search, Calendar, Download, Mail, X } from "lucide-react";
import DataTable from "react-data-table-component";
import { useNavigate } from "react-router-dom";
import Modal from "react-modal";
import { adminServices } from "../services/AdminServices";
import LoadingModal from "../components/LoadingModal";
import { toast } from "react-toastify";
import { logExport } from "../services/ExportLogService";

const BucorModule = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedFacility, setSelectedFacility] = useState("All Facilities");
  const [selectedMerchant, setSelectedMerchant] = useState("All Merchant");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [dateTime, setDateTime] = useState({ date: "", time: "" });
  const [notificationFilter, setNotificationFilter] = useState("All");
  const [showNotifications, setShowNotifications] = useState(false);
  const [bucorApprovals, setBucorApprovals] = useState([]);
  const [dashboardCounts, setDashboardCounts] = useState({
    pendingRequests: 0,
    approvedRequests: 0,
    specialOrders: 0,
    itemApproval: 0
  });
  const [loading, setLoading] = useState(false);
  const [viewLoadingRequestNo, setViewLoadingRequestNo] = useState(null);
  const [activeCard, setActiveCard] = useState("all"); // 'all' | 'pending' | 'approved' | 'itemApproval' | 'specialOrder'

  // Get current date and time
  const getCurrentDateTime = () => {
    const now = new Date();
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    
    const dayName = days[now.getDay()];
    const month = months[now.getMonth()];
    const day = now.getDate();
    const year = now.getFullYear();
    
    let hours = now.getHours();
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');
    const ampm = hours >= 12 ? 'pm' : 'am';
    hours = hours % 12;
    hours = hours ? hours : 12;
    const hoursStr = String(hours).padStart(2, '0');
    
    return {
      date: `${dayName}, ${month} ${day}, ${year}`,
      time: `${hoursStr}:${minutes}:${seconds} ${ampm}`
    };
  };

  useEffect(() => {
    // Set initial time
    setDateTime(getCurrentDateTime());
    
    // Update time every second
    const interval = setInterval(() => {
      setDateTime(getCurrentDateTime());
    }, 1000);

    // Fetch Bucor Approvals
    fetchBucorApprovals();

    // Cleanup interval on component unmount
    return () => clearInterval(interval);
  }, []);

  const mapApprovalToRow = (approval, idx) => {
    const formatDate = (dateString) => {
      if (!dateString) return "";
      const date = new Date(dateString);
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    };
    const requestType = approval.requestType ?? (
      approval.bucorApprovalItems && approval.bucorApprovalItems.length > 0
        ? "Item approval"
        : "Special Order Request"
    );
    const requestId = approval.requestId ?? approval.requestNo ?? `REQ-${approval.id ?? idx}`;
    return {
      id: approval.id ?? requestId,
      date: formatDate(approval.date ?? approval.regDate),
      requestId,
      requestType,
      requisitor: approval.requisitor ?? approval.requestedByUser ?? "IPX Admin",
      facility: approval.facility ?? "-",
      status: approval.status ?? "Pending",
      notes: approval.notesRemarks ?? approval.remarks ?? "No remarks",
      orderId: approval.orderId,
      requestedBy: approval.requestedBy,
      originalData: approval
    };
  };

  const buildSearchParams = (cardFilter = activeCard) => {
    const params = new URLSearchParams();
    if (searchQuery.trim()) params.set("keyword", searchQuery.trim());
    if (dateFrom) params.set("dateFrom", dateFrom);
    if (dateTo) params.set("dateTo", dateTo);
    if (selectedFacility && selectedFacility !== "All Facilities") params.set("facility", selectedFacility);
    if (selectedMerchant && selectedMerchant !== "All Merchant") params.set("merchantCode", selectedMerchant);
    if (cardFilter === "pending") params.set("status", "Pending");
    else if (cardFilter === "approved") params.set("status", "Approved");
    else if (cardFilter === "itemApproval") params.set("requestType", "Item approval");
    else if (cardFilter === "specialOrder") params.set("requestType", "Special Order Request");
    params.set("page", "1");
    params.set("limit", "100");
    return params.toString();
  };

  const fetchBucorApprovals = async (cardFilter = activeCard) => {
    setLoading(true);
    try {
      const qs = buildSearchParams(cardFilter);
      const [dashboardRes, listRes] = await Promise.all([
        adminServices.getData('BucorApprovals/dashboard'),
        adminServices.getData(`BucorApprovals/search${qs ? "?" + qs : ""}`)
      ]);

      if (dashboardRes?.data && typeof dashboardRes.data === 'object' && !Array.isArray(dashboardRes.data)) {
        setDashboardCounts({
          pendingRequests: dashboardRes.data.pendingRequests ?? 0,
          approvedRequests: dashboardRes.data.approvedRequests ?? 0,
          specialOrders: dashboardRes.data.specialOrders ?? 0,
          itemApproval: dashboardRes.data.itemApproval ?? 0
        });
      }

      const listData = listRes?.data;
      const raw = Array.isArray(listData) ? listData : (listData?.items ?? listData?.data ?? listData?.results ?? []);
      const mappedData = raw.map((item, idx) => mapApprovalToRow(item, idx));

      setBucorApprovals(mappedData);
    } catch (error) {
      console.error("Error fetching Bucor Approvals:", error);
      toast.error("Failed to load approvals. Please try again.");
      setBucorApprovals([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    fetchBucorApprovals(activeCard);
  };

  const handleViewClick = async (row) => {
    const requestNo = row.requestId ?? row.originalData?.requestId ?? row.originalData?.requestNo;
    if (!requestNo) {
      toast.error("Request number not found");
      return;
    }
    setViewLoadingRequestNo(requestNo);
    try {
      const res = await adminServices.getBucorApprovalByRequestNo(requestNo);
      const detailData = res?.data ?? res;
      if (row.requestType === "Item approval") {
        navigate("/bucor-item-approval-detail", { state: { requestData: detailData } });
      } else {
        navigate("/bucor-order-detail", { state: { orderData: detailData } });
      }
    } catch (err) {
      console.error("Failed to fetch approval details:", err);
      toast.error(err.response?.data?.message || "Failed to load approval details.");
    } finally {
      setViewLoadingRequestNo(null);
    }
  };


  // Sample notifications data
  const notificationsData = [
    {
      id: 1,
      title: "Special Order Approved",
      description: "[OS-2025-001] User approved a request. Click here to view full details.",
      timestamp: "2s",
      isUnread: true
    },
    {
      id: 2,
      title: "Special Order Rejected",
      description: "[OS-2025-001] User disapproved a request. Click here to view full details.",
      timestamp: "2s",
      isUnread: true
    },
    {
      id: 3,
      title: "Item Approval",
      description: "[REQ-2025-002] IPX Admin submitted a New Item Approval. Click here to view full details.",
      timestamp: "2s",
      isUnread: true
    },
    {
      id: 4,
      title: "Special Order",
      description: "[OS-2025-001] Merchant Name submitted a Special Order. Click here to view full details.",
      timestamp: "2s",
      isUnread: true
    },
    {
      id: 5,
      title: "Special Order",
      description: "[OS-2025-001] Merchant Name updated an item in a Special Order. Click here to view full details.",
      timestamp: "2s",
      isUnread: true
    }
  ];

  const filteredNotifications = notificationFilter === "Unread" 
    ? notificationsData.filter(n => n.isUnread)
    : notificationsData;

  const summaryCounts = useMemo(() => ({
    pending: dashboardCounts.pendingRequests,
    approved: dashboardCounts.approvedRequests,
    itemApproval: dashboardCounts.itemApproval,
    specialOrder: dashboardCounts.specialOrders
  }), [dashboardCounts]);

  // Client-side card filter as fallback in case API doesn't support all filter params
  const filteredData = useMemo(() => {
    let list = bucorApprovals.filter((r) => r.status && r.status.trim() !== "");
    if (activeCard === "pending") {
      list = list.filter((r) => ["pending", "new"].includes((r.status || "").toLowerCase()));
    } else if (activeCard === "approved") {
      list = list.filter((r) => ["approved", "active"].includes((r.status || "").toLowerCase()));
    } else if (activeCard === "itemApproval") {
      list = list.filter((r) => (r.requestType || "").toLowerCase().includes("item"));
    } else if (activeCard === "specialOrder") {
      list = list.filter((r) =>
        (r.requestType || "").toLowerCase().includes("special") ||
        (r.status || "").toUpperCase() === "SPECIAL_ORDER"
      );
    }
    return list;
  }, [bucorApprovals, activeCard]);

  const handleCardClick = (card) => {
    setActiveCard(card);
    fetchBucorApprovals(card);
  };

  const getStatusBadgeClass = (status) => {
    switch ((status || "").toUpperCase()) {
      case "PENDING":
      case "Pending":
        return "bucor-status-pending";
      case "APPROVED":
      case "Approved":
      case "ACTIVE":
        return "bucor-status-approved";
      case "REJECTED":
      case "Rejected":
        return "bucor-status-rejected";
      case "NEW":
        return "bucor-status-new";
      case "SPECIAL_ORDER":
      case "Special Order":
        return "bucor-status-special";
      default:
        return "bucor-status-pending";
    }
  };

  const columns = [
    {
      name: "",
      cell: row => (
        <input type="checkbox" className="bucor-table-checkbox" />
      ),
      width: "50px",
      sortable: false,
      center: true
    },
    {
      name: "Date",
      selector: row => row.date || "-",
      sortable: true,
      width: "120px",
      center: true
    },
    {
      name: "Request ID",
      selector: row => row.requestId || "-",
      sortable: true,
      width: "150px",
      center: true
    },
    {
      name: "Request Type",
      selector: row => row.requestType || "-",
      sortable: true,
      width: "180px",
      center: true
    },
    {
      name: "Requisitor",
      selector: row => row.requisitor || "-",
      sortable: true,
      width: "150px",
      center: true
    },
    {
      name: "Facility",
      selector: row => row.facility || "-",
      sortable: true,
      width: "150px",
      center: true
    },
    {
      name: "Status",
      cell: row => {
        if (!row.status) return <div>-</div>;
        return (
          <span className={`bucor-status-badge ${getStatusBadgeClass(row.status)}`}>
            {row.status}
          </span>
        );
      },
      sortable: true,
      width: "250px",
      center: true
    },
    {
      name: "Notes/Remarks",
      selector: row => row.notes || "-",
      sortable: false,
      width: "250px",
      center: true
    },
    {
      name: "Action",
      cell: row => {
        if (!row.status) return <div>-</div>;
        const requestNo = row.requestId ?? row.originalData?.requestId;
        const isLoading = viewLoadingRequestNo === requestNo;
        return (
          <button
            className="bucor-view-btn"
            disabled={isLoading}
            onClick={() => handleViewClick(row)}
          >
            {isLoading ? "Loading..." : "View"}
          </button>
        );
      },
      sortable: false,
      width: "100px",
      center: true
    }
  ];

  const customStyles = {
    table: {
      style: {
        tableLayout: 'auto'
      }
    },
    headRow: {
      style: {
        backgroundColor: "#002347",
        color: "#ffffff",
        fontSize: "0.875rem",
        fontWeight: 600,
        minHeight: "48px"
      }
    },
    headCells: {
      style: {
        paddingLeft: "16px",
        paddingRight: "16px",
        textTransform: "uppercase",
        textAlign: "center",
        justifyContent: "center"
      }
    },
    rows: {
      style: {
        minHeight: "56px"
      }
    },
    cells: {
      style: {
        paddingLeft: "16px",
        paddingRight: "16px",
        paddingTop: "12px",
        paddingBottom: "12px",
        textAlign: "center",
        justifyContent: "center"
      }
    }
  };
  const handleExportToCSV = async () => {
    if (!filteredData || filteredData.length === 0) {
      toast.warning("No data to export");
      return;
    }

    try {
      await logExport('BuCor Module');

      const headers = [
        'Request ID',
        'Merchant Name / Requisitor',
        'Request Date',
        'Request Type',
        'Facility',
        'Status',
        'Classification',
        'Notes / Remarks',
        'Created By',
        'Approved By',
        'Timestamp'
      ];

      const rows = filteredData.map(item => [
        item.requestId || '',
        item.requisitor || item.originalData?.requestedByUser || item.originalData?.merchantName || '',
        item.date || item.originalData?.regDate || item.originalData?.requestDate || '',
        item.requestType || '',
        item.facility || item.originalData?.facility || '',
        item.status || '',
        item.originalData?.classification || item.originalData?.itemType || '',
        item.notes || item.originalData?.notesRemarks || item.originalData?.remarks || '',
        item.originalData?.createdBy || item.originalData?.requestedByUser || '',
        item.originalData?.approvedBy || item.originalData?.approvedByUser || '',
        item.originalData?.updatedAt || item.originalData?.lastUpdated || item.date || ''
      ]);

      const csvContent = [headers, ...rows]
        .map(row => row.map(field => `"${String(field ?? '').replace(/"/g, '""')}"`).join(','))
        .join('\n');

      const blob = new Blob(["\uFEFF" + csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `BucorApprovals_${new Date().toISOString().slice(0,10)}.csv`);
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success('Approvals exported successfully');
    } catch (err) {
      console.error('Export error:', err);
      toast.error('Failed to export. Please try again.');
    }
  };
  return (
    <div className="dashboard-content">
      <LoadingModal show={loading} message="Loading BuCor Approvals..." />
      <div className="bucor-module-page">
        {/* Header Section */}
        <div className="bucor-header">
          <div className="bucor-header-left">
            <div className="bucor-header-icon">
              <div className="bucor-icon-square"></div>
            </div>
            <div className="bucor-title-section">
              <div className="bucor-title-with-icon">
                <h1 className="bucor-title">Approval Dashboard</h1>
              </div>
              <p className="bucor-subtitle">Bureau of Corrections - BuCor Agent</p>
            </div>
          </div>
          <div className="bucor-header-right">
            <div className="bucor-datetime">
              <div className="bucor-date">{dateTime.date}</div>
              <div className="bucor-time">{dateTime.time}</div>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="bucor-summary-cards">
          <div
            className={`bucor-summary-card bucor-summary-card-clickable${activeCard === "pending" ? " bucor-summary-card-active" : ""}`}
            onClick={() => handleCardClick("pending")}
            title="Show Pending Requests"
          >
            <div className="bucor-card-icon orange">
              <Clock size={24} />
            </div>
            <div className="bucor-card-content">
              <div className="bucor-card-title">Pending Requests</div>
              <div className="bucor-card-value">{summaryCounts.pending}</div>
            </div>
          </div>
          <div
            className={`bucor-summary-card bucor-summary-card-clickable${activeCard === "approved" ? " bucor-summary-card-active" : ""}`}
            onClick={() => handleCardClick("approved")}
            title="Show Approved Requests"
          >
            <div className="bucor-card-icon green">
              <CheckCircle size={24} />
            </div>
            <div className="bucor-card-content">
              <div className="bucor-card-title">Approved Requests</div>
              <div className="bucor-card-value">{summaryCounts.approved}</div>
            </div>
          </div>
          <div
            className={`bucor-summary-card bucor-summary-card-clickable${activeCard === "itemApproval" ? " bucor-summary-card-active" : ""}`}
            onClick={() => handleCardClick("itemApproval")}
            title="Show Item Approvals"
          >
            <div className="bucor-card-icon yellow">
              <Folder size={24} />
            </div>
            <div className="bucor-card-content">
              <div className="bucor-card-title">Item Approval</div>
              <div className="bucor-card-value">{summaryCounts.itemApproval}</div>
            </div>
          </div>
          <div
            className={`bucor-summary-card bucor-summary-card-clickable${activeCard === "specialOrder" ? " bucor-summary-card-active" : ""}`}
            onClick={() => handleCardClick("specialOrder")}
            title="Show Special Orders"
          >
            <div className="bucor-card-icon blue">
              <FileText size={24} />
            </div>
            <div className="bucor-card-content">
              <div className="bucor-card-title">Special Order</div>
              <div className="bucor-card-value">{summaryCounts.specialOrder}</div>
            </div>
          </div>
        </div>

        {/* Filter and Search Section */}
        <div className="bucor-filter-section">
          <div className="bucor-search-container">
            <Search className="bucor-search-icon" size={18} />
            <input
              type="text"
              placeholder="Search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              className="bucor-search-input"
            />
            <button type="button" className="bucor-search-btn" onClick={handleSearch}>
              Search
            </button>
          </div>
          <select
            className="bucor-filter-select"
            value={selectedFacility}
            onChange={(e) => setSelectedFacility(e.target.value)}
          >
            <option>All Facilities</option>
          </select>
          <select
            className="bucor-filter-select"
            value={selectedMerchant}
            onChange={(e) => setSelectedMerchant(e.target.value)}
          >
            <option>All Merchant</option>
          </select>
          <div className="bucor-date-input-container">
            <Calendar className="bucor-date-icon" size={18} />
            <input
              type="date"
              className="bucor-date-input"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              placeholder="From"
            />
          </div>
          <div className="bucor-date-input-container">
            <Calendar className="bucor-date-icon" size={18} />
            <input
              type="date"
              className="bucor-date-input"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              placeholder="To"
            />
          </div>
          <button className="bucor-export-btn" onClick={handleExportToCSV}>
            <Download size={18} />
            Export as CSV
          </button>
        </div>

        {/* Table Section */}
        <div className="bucor-table-section">
          <div className="bucor-table-header-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
              {activeCard !== 'all' && (
                <span>
                  Filtering: <strong style={{ color: '#002347' }}>
                    {activeCard === 'pending' && 'Pending Requests'}
                    {activeCard === 'approved' && 'Approved Requests'}
                    {activeCard === 'itemApproval' && 'Item Approvals'}
                    {activeCard === 'specialOrder' && 'Special Orders'}
                  </strong>
                  {' '}({filteredData.length} record{filteredData.length !== 1 ? 's' : ''})
                  <button
                    onClick={() => handleCardClick('all')}
                    style={{ marginLeft: '10px', background: 'none', border: '1px solid #d1d5db', borderRadius: '4px', padding: '2px 8px', cursor: 'pointer', fontSize: '0.75rem', color: '#6b7280' }}
                  >
                    Clear filter
                  </button>
                </span>
              )}
              {activeCard === 'all' && (
                <span>{filteredData.length} total record{filteredData.length !== 1 ? 's' : ''}</span>
              )}
            </div>
          </div>
          <div className="bucor-table-container">
            <DataTable
              columns={columns}
              data={filteredData}
              customStyles={customStyles}
              striped
              highlightOnHover
              noHeader={false}
              pagination={false}
              noDataComponent={
                <div style={{ 
                  padding: '40px 20px', 
                  textAlign: 'center',
                  color: '#666',
                  fontSize: '1rem'
                }}>
                  No record found
                </div>
              }
            />
          </div>
        </div>

        {/* Notifications Modal */}
        <Modal
          isOpen={showNotifications}
          onRequestClose={() => setShowNotifications(false)}
          className="bucor-notifications-modal"
          overlayClassName="bucor-notifications-modal-overlay"
          ariaHideApp={false}
        >
          <div className="bucor-notifications-modal-content">
            <div className="bucor-notifications-modal-header">
              <h3 className="bucor-notifications-title">Notifications</h3>
              <button 
                className="bucor-notifications-close-btn"
                onClick={() => setShowNotifications(false)}
              >
                <X size={20} />
              </button>
            </div>
            <div className="bucor-notifications-filters">
              <button 
                className={`bucor-notification-filter-btn ${notificationFilter === "All" ? "active" : ""}`}
                onClick={() => setNotificationFilter("All")}
              >
                All
              </button>
              <button 
                className={`bucor-notification-filter-btn ${notificationFilter === "Unread" ? "active" : ""}`}
                onClick={() => setNotificationFilter("Unread")}
              >
                Unread
              </button>
            </div>
            <div className="bucor-notifications-list">
              {filteredNotifications.length > 0 ? (
                filteredNotifications.map((notification) => (
                  <div key={notification.id} className="bucor-notification-item">
                    <div className="bucor-notification-content">
                      <div className="bucor-notification-title">{notification.title}</div>
                      <div className="bucor-notification-description">{notification.description}</div>
                    </div>
                    <div className="bucor-notification-right">
                      <div className="bucor-notification-time">{notification.timestamp}</div>
                      {notification.isUnread && (
                        <div className="bucor-notification-dot"></div>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div className="bucor-notifications-empty">
                  <div className="bucor-notifications-empty-content"></div>
                </div>
              )}
            </div>
          </div>
        </Modal>
      </div>
    </div>
  );
};

export default BucorModule;

